import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { reportError, detectErrorType } from "./lib/errorReporter";
import { supabase } from "./integrations/supabase/client";
import { safeLocalStorage } from "./lib/safeStorage";

// Sentry deve ser inicializado antes de tudo
initSentry();

// ============================================
// FASE 5.1: PRELOAD DE DADOS CRÍTICOS
// Inicia fetch de dados do usuário antes do React renderizar
// ============================================
const preloadUserData = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    // Se não tem sessão válida, limpar qualquer resíduo de sessão anterior
    if (!session?.user) {
      safeLocalStorage.removeItem("current-org-id");
      safeLocalStorage.removeItem("current-pipeline-id");
      safeLocalStorage.removeItem("current-partner-id");
      console.debug("[Preload] Sem sessão, localStorage limpo");
      return;
    }

    // Query direta em org_members (RPC get_user_org_context não existe)
    const { data: orgMember } = await supabase
      .from("org_members")
      .select("org_id")
      .eq("user_id", session.user.id)
      .limit(1)
      .maybeSingle();

    // Cachear org_id no localStorage para acesso rápido
    if (orgMember?.org_id) {
      const storedOrgId = safeLocalStorage.getItem("current-org-id");
      if (!storedOrgId) {
        safeLocalStorage.setItem("current-org-id", orgMember.org_id);
      }
    }
  } catch (error) {
    // Falha silenciosa - dados serão buscados normalmente pelos hooks
    console.debug("Preload failed, will fetch on demand:", error);
  }
};

// Iniciar preload imediatamente (não bloqueia render)
preloadUserData();

// Captura global de erros síncronos
window.addEventListener('error', (event) => {
  const errorType = event.error ? detectErrorType(event.error) : 'error';
  
  reportError({
    error_type: errorType,
    message: event.message || 'Unknown error',
    stack: event.error?.stack,
    meta: {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    },
  });
});

// Captura global de promises rejeitadas
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const errorType = detectErrorType(error);
  
  reportError({
    error_type: errorType === 'unknown' ? 'unhandledrejection' : errorType,
    message: error?.message || String(error),
    stack: error?.stack,
  });
});

// Web Vitals são coletados pelo RumProvider com persistência no DB
// Feature flag: VITE_ENABLE_RUM=true

createRoot(document.getElementById("root")!).render(<App />);
